import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import mqtt, { MqttClient } from 'mqtt/dist/mqtt';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../shared/notification.service';
import { SystemInfoService } from '../services/system-info.service';

@Component({
    selector: 'app-system-list',
    templateUrl: './system-list.component.html',
    styleUrls: ['./system-list.component.css']
})
export class SystemListComponent implements OnInit, OnDestroy {
    systems$: Observable<any[]> | null = null;
    systems: any[] = [];
    systemSubscription: any = null;

    rowActionButtons: { icon: string; tooltip: string; eventEmitter: any; }[] = [];

    onOpenServicePopup: EventEmitter<any> = new EventEmitter<any>();
    servicePopupSubscription: any = null;

    columnSettings = [
        { columnId: 'systemId', name: 'System ID' },
        { columnId: 'jsdversion', name: 'Version' }
    ];
    private mqttClient: MqttClient | null = null;
    private destroy$ = new Subject<void>();
    private mqttClientId = Math.random().toString(16).substr(2, 8);
    private serverUsed = String(Math.floor(Math.random() * 2) + 1);

    private updateTimer: any = null;

    dataSource$: Subject<any[]> = new Subject<any[]>();
    availableKeys$: Subject<string[]> = new Subject<string[]>();
    currentKey = 'version';

    private systemInfo = {};

    visibleColumnsControl = new FormControl<string[]>([], { nonNullable: true });
    allHeaders: string[] = [];

    selectAllValue = '__all__';

    private previousHeaders: string[] = [];

    constructor(
        private auth: AuthService,
        private notificationService: NotificationService,
        private systemInfoService: SystemInfoService
    ) {
        this.systems$ = systemInfoService.systemInfo();

        this.systemSubscription = this.systems$.subscribe(systems => {
            this.systems = systems;
        });

        this.servicePopupSubscription = this.onOpenServicePopup.subscribe(event => {
            const number = event.eventData.systemNumber;
            const systemId = this.getSystemId(number);

            this.openPopupShell(systemId);
        });

        this.rowActionButtons = [
            { icon: 'terminal', tooltip: 'Service Popup', eventEmitter: this.onOpenServicePopup }
        ];
    }

    ngOnInit() {
        this.connectToMqtt();
        this.visibleColumnsControl.valueChanges.subscribe(() => this.updateTable());
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.systemSubscription) {
            this.systemSubscription.unsubscribe();
        }

        if (this.servicePopupSubscription) {
            this.servicePopupSubscription.unsubscribe();
        }

        if (this.mqttClient) {
            this.mqttClient.end(true);
            this.mqttClient = null;
        }
    }

    async connectToMqtt() {
        let token: any = null;
        try {
            token = await this.auth.getToken();
        } catch {
            token = null;
        }

        if (!token || typeof token !== 'string') {
            this.notificationService.showNotification('error', 'Unable to connect: missing auth token.');
            return;
        }

        this.mqttClient = mqtt.connect(`wss://prod${this.serverUsed}.friggkantine.no:9091/?token=${token}`,
            {
                keepalive: 30,
                clientId: 'frigg-admin-system-list-' + this.mqttClientId
            }
        );
        this.mqttClient.on('connect', () => {
            console.log('Connected to MQTT broker');
            this.mqttClient?.subscribe('systems/+/status/#');
        });
        let count = 0;
        this.mqttClient.on('message', (topic, message) => {
            count++;
            // Example topic: systems/9000/status/modem/connection
            const regex = /^systems\/([0-9]+)\/status\/([a-zA-Z0-9/]+)$/;
            const match = topic.match(regex);
            // console.log(count, 'Received MQTT message:', topic, message.toString(), match);
            if (match) {
                // console.log(count);
                // console.log("System: " + match[1] + ", Status: " + match[2]);


                const systemNumber = match[1];
                const data = JSON.parse(message.toString());

                const key = match[2].replace(/\//g, '-'); // Replace slashes with underscores for valid object keys


                if (!this.systemInfo[systemNumber]) {
                    this.systemInfo[systemNumber] = {};
                }

                // Flatten object structure so we can easily display in table
                const newData = Object.keys(data).reduce((acc, k) => {
                    if (typeof data[k] === 'object' && data[k] !== null) {
                        Object.keys(data[k]).forEach(subKey => {
                            acc[`${k}-${subKey}`] = data[k][subKey];
                        });
                    } else {
                        acc = { ...acc, [k]: data[k] };
                    }
                    return acc;
                }, {});



                this.systemInfo[systemNumber][key] = { ...this.systemInfo[systemNumber][key], ...newData };

                if (this.updateTimer) {
                    clearTimeout(this.updateTimer);
                }

                this.updateTimer = setTimeout(this.updateTable.bind(this), 500);
            }
        });
    }

    updateTable() {
        console.log(this.systemInfo)
        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
        }

        // Remove inactive systems from the table
        Object.keys(this.systemInfo).forEach(key => {
            if (!this.isActive(key)) {
                delete this.systemInfo[key];
            }
        });

        this.availableKeys$.next(Array.from(
            Object.keys(this.systemInfo).reduce((acc, key) => {
                Object.keys(this.systemInfo[key]).forEach(k => acc.add(k));
                return acc;
            }, new Set<string>())
        ));

        const headers = Object.keys(this.systemInfo).map(key => this.systemInfo[key][this.currentKey] ? Object.keys(this.systemInfo[key][this.currentKey]) : []).flat().reduce((acc, key) => {
            acc.add(key);
            return acc;
        }, new Set<string>());

        const allHeaders = Array.from(headers);
        this.allHeaders = allHeaders;
        // Only auto-select all columns if the available columns have changed
        if (
            (!this.visibleColumnsControl.value || this.visibleColumnsControl.value.length === 0) &&
            (this.previousHeaders.length !== allHeaders.length || !this.previousHeaders.every((h, i) => h === allHeaders[i]))
        ) {
            this.visibleColumnsControl.setValue(allHeaders, { emitEvent: false });
        }
        this.previousHeaders = [...allHeaders];
        this.columnSettings = [
            { columnId: 'systemNumber', name: 'Number' },
            { columnId: 'systemName', name: 'Name' },
            ...allHeaders
                .filter(h => this.visibleColumnsControl.value?.includes(h))
                .map(h => ({ columnId: h, name: h }))
        ];

        this.dataSource$.next(Object.keys(this.systemInfo).map(key => {
            const selectedInfo = this.systemInfo[key][this.currentKey] || {};
            const values = Array.from(headers).reduce((acc, h) => {
                acc[h] = selectedInfo[h] !== undefined ? selectedInfo[h] : null;
                return acc;
            }, {});

            return {
                systemNumber: key,
                systemName: this.getSystemName(key),
                ...values
            };
        }));

    }

    getSystemName(systemNumber: string) {
        const system = this.systems.find(s => parseInt(s.number, 10) === parseInt(systemNumber, 10));
        return system ? system.name : 'Unknown System';
    }

    getSystemId(systemNumber: string) {
        const system = this.systems.find(s => parseInt(s.number, 10) === parseInt(systemNumber, 10));
        return system ? system.systemId : 'Unknown System';
    }

    isActive(systemNumber: string) {
        const system = this.systems.find(s => parseInt(s.number, 10) === parseInt(systemNumber, 10));
        return system ? system.active : false;
    }

    onKeyChange() {
        // Reset visibleColumnsControl to all columns for the new info key
        const headers = Object.keys(this.systemInfo).map(key => this.systemInfo[key][this.currentKey] ? Object.keys(this.systemInfo[key][this.currentKey]) : []).flat();
        this.visibleColumnsControl.setValue(headers, { emitEvent: false });
        this.updateTable();
    }

    openPopupShell(systemId: string) {
        // Open dialog
        window.open(`/support/shell/${systemId}`, '_blank', 'width=1250,height=800,location=no,menubar=no,toolbar=no');
    }

    isAllSelected(): boolean {
        return this.visibleColumnsControl.value && this.allHeaders.length > 0 && this.visibleColumnsControl.value.length === this.allHeaders.length;
    }

    toggleSelectAll(): void {
        if (this.isAllSelected()) {
            console.log("Deselecting all columns");
            this.visibleColumnsControl.setValue([], { emitEvent: true });
        } else {
            console.log("Selecting all columns");
            this.visibleColumnsControl.setValue([...this.allHeaders], { emitEvent: true });
        }
    }

    onSelectAllChange(event: any): void {
        if (event.isUserInput) {
            if (this.isAllSelected()) {
                this.visibleColumnsControl.setValue([], { emitEvent: true });
            } else {
                this.visibleColumnsControl.setValue([...this.allHeaders], { emitEvent: true });
            }
        }
    }
}
